import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/hooks/useDeviceLive";
import { useRegion } from "@/context/RegionContext";
import CatalogTable from "@/components/settings/CatalogTable";
import CatalogForm from "@/components/settings/CatalogForm";
import SizingParams from "@/components/settings/SizingParams";
import RegionsPanel from "@/components/settings/RegionsPanel";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const TABS = [["panel", "Solar panels"], ["inverter", "Inverters"], ["battery", "Batteries"], ["regions", "Countries & currencies"], ["params", "Engine parameters"]];

export default function Settings() {
  const { regions, active, byCode, reloadRegions } = useRegion();
  const [tab, setTab] = useState("panel");
  const [invRegion, setInvRegion] = useState(null);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const regionCode = invRegion && byCode[invRegion] ? invRegion : active !== "ALL" && byCode[active] ? active : (regions.find((r) => r.is_default) || regions[0])?.code;
  const region = byCode[regionCode];

  const load = useCallback(() => api.get("/catalog").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  useInvalidate(["catalog"], load);
  useInvalidate(["regions"], reloadRegions);

  const remove = async () => {
    try { await api.delete(`/catalog/${removing.id}`); toast.success("Removed from catalog"); setRemoving(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const isCatalog = ["panel", "inverter", "battery"].includes(tab);

  return (
    <div className="h-full grid grid-cols-[260px_1fr]" data-testid="settings-page">
      <aside className="border-r border-kl-line bg-kl-black p-6 flex flex-col">
        <p className="kl-label-dark">Configuration</p>
        <h1 className="font-display font-light text-2xl mt-1 mb-8">Catalog & settings</h1>
        <nav className="space-y-px">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`settings-tab-${k}`} className={`w-full text-left px-4 h-10 text-sm border-l-2 transition-colors duration-150 ${tab === k ? "border-kl-green bg-kl-surface text-white" : "border-transparent text-kl-muted hover:text-white hover:bg-kl-surface"}`}>{l}</button>
          ))}
        </nav>
        {isCatalog && (
          <div className="mt-8 border border-kl-line">
            <div className="px-3 py-2 kl-label-dark border-b border-kl-line">Inventory region</div>
            {regions.map((r) => (
              <button key={r.code} onClick={() => setInvRegion(r.code)} data-testid={`settings-region-${r.code}`} className={`w-full flex items-center justify-between px-3 h-9 text-sm transition-colors duration-150 ${regionCode === r.code ? "bg-white text-kl-black" : "text-kl-muted hover:bg-kl-surface hover:text-white"}`}>
                <span>{r.name}</span><span className="font-mono text-[11px]">{r.currency}</span>
              </button>
            ))}
          </div>
        )}
        <p className="mt-auto text-xs text-kl-muted leading-relaxed">Each country keeps its own inventory and local prices. A device uses the inventory of its country automatically, or a manual override set on its dashboard.</p>
      </aside>
      <section className="bg-white text-kl-black overflow-y-auto kl-scroll-light p-8">
        {tab === "params" && <SizingParams />}
        {tab === "regions" && <RegionsPanel />}
        {isCatalog && region && (
          <CatalogTable type={tab} region={region} items={items.filter((i) => i.type === tab && i.region === region.code)} onAdd={() => setEditing({ type: tab, region: region.code })} onEdit={setEditing} onRemove={setRemoving} />
        )}
      </section>
      <CatalogForm item={editing} region={editing ? byCode[editing.region] : null} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Remove from catalog?" description={removing ? `${removing.brand} ${removing.model} (${removing.region})` : ""} confirmLabel="Remove" onConfirm={remove} />
    </div>
  );
}
